
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

export interface ProcessStep {
  id: string;
  title: string;
  description?: string;
  content?: any;
  component?: any;
  props?: Record<string, unknown> | (() => Record<string, unknown>);
  listeners?: Record<string, (...args: any[]) => void>;
  canContinue?: boolean | (() => boolean);
  onContinue?: () => void | Promise<void>;
}

/**
 * ProcessStepManager - Multi-step form wizard with sidebar support
 *
 * Time: O(n) where n = number of steps
 * Space: O(n)
 */
@Component({})
export default class ProcessStepManager extends Vue {
  @Prop({ type: Array, required: true })
  steps!: ProcessStep[];

  @Prop({ type: Function })
  onStepChange?: (step: number) => void;

  @Prop({ type: String })
  persistenceKey?: string;

  @Prop({ type: Boolean, default: false })
  showStepMetadata!: boolean;

  currentStep: number = 1;

  get containerRef(): HTMLDivElement | null {
    return this.$refs.containerRef as HTMLDivElement | null;
  }

  get canContinue(): boolean {
    // Cache steps to avoid reactive loop
    const steps = this.steps;
    if (!steps || steps.length === 0) return false;
    
    const step = steps[this.currentStep - 1];
    if (!step) return false;

    if (typeof step.canContinue === 'function') {
      try {
        return step.canContinue();
      } catch (e) {
        console.error('Error in canContinue function:', e);
        return false;
      }
    }
    return step.canContinue !== false;
  }

  resolveStepProps(step: ProcessStep): Record<string, unknown> {
    if (!step.props) return {};
    return typeof step.props === 'function' ? step.props() : step.props;
  }

  isComponent(value: unknown): boolean {
    return Boolean(value && (typeof value === 'object' || typeof value === 'function'));
  }

  mounted(): void {
    this.currentStep = 1;

    if (this.persistenceKey) {
      const savedStep = localStorage.getItem(`${this.persistenceKey}_currentStep`);
      if (savedStep) {
        const step = parseInt(savedStep, 10);
        if (step >= 1 && step <= this.steps.length) {
          this.currentStep = step;
        }
      }
    }
  }

  @Watch('currentStep')
  onCurrentStepChange(): void {
    if (this.persistenceKey) {
      localStorage.setItem(
        `${this.persistenceKey}_currentStep`,
        this.currentStep.toString()
      );
    }

    if (this.onStepChange) {
      this.onStepChange(this.currentStep);
    }

    this.$nextTick(() => {
      setTimeout(() => {
        const stepElement = document.getElementById(`step-${this.currentStep}-section`);
        if (stepElement) {
          stepElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    });
  }

  handleStepClick(step: number): void {
    if (step <= this.currentStep) {
      this.currentStep = step;
    }
  }

  async handleContinue(): Promise<void> {
    const step = this.steps[this.currentStep - 1];
    if (!step) return;

    let shouldAutoAdvance = true;
    if (step.onContinue) {
      try {
        const result = await step.onContinue();
        // If handler returns false, don't auto-advance
        if (result === false) {
          shouldAutoAdvance = false;
        }
      } catch (e) {
        console.error('Error in onContinue handler:', e);
        shouldAutoAdvance = false;
      }
    }

    // Auto-advance if handler didn't explicitly return false
    if (shouldAutoAdvance && this.currentStep < this.steps.length) {
      this.currentStep += 1;
    } else if (this.currentStep >= this.steps.length) {
      this.$emit('process-complete');
    }
  }

  setCurrentStep(step: number): void {
    this.currentStep = step;
  }

  get hasSidebarSlot(): boolean {
    return Boolean(this.$slots.sidebar || this.$scopedSlots.sidebar);
  }
}
